/**
 * Transaction Board Service
 * 
 * Wraps Firestore transaction utilities for atomic board state updates.
 * Provides methods for updating employee states, saving full board state,
 * and reordering queue with atomic operations.
 * 
 * Eliminates race conditions in concurrent manager edits by ensuring
 * read-modify-write atomicity across multiple documents.
 * 
 * @module transaction-board.service
 */

import { Injectable } from '@angular/core';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import {
  batchTransactionUpdate,
  BatchUpdateItem,
  TransactionOptions,
} from './transaction-utils';

/**
 * Employee state type
 */
export interface EmployeeData {
  state?: string;
  order?: number;
  email?: string;
  [key: string]: any;
}

@Injectable({ providedIn: 'root' })
export class TransactionBoardService {
  constructor(private db: AngularFirestore) {}

  /**
   * Update a single employee's state with atomic transaction
   * 
   * @param boardId Board document ID
   * @param empEmail Employee email (document ID)
   * @param newState New state value (e.g., 'up', 'break', 'with_guest')
   * @param options Transaction configuration options
   * @returns Promise resolving when transaction completes
   * 
   * @throws {TransactionError} If document not found or transaction fails after retries
   */
  async updateEmployeeState(
    boardId: string,
    empEmail: string,
    newState: string,
    options?: TransactionOptions
  ): Promise<void> {
    const firestoreInstance = this.db.firestore as any;
    const logPrefix = options?.logPrefix || 'UpdateEmployeeState';
    
    console.log(`[Transaction:${logPrefix}] boardId=${boardId} operation=updateEmployeeState empEmail=${empEmail} Starting...`);

    const docPath = `boards/${boardId}/users/${empEmail}`;
    const updates: BatchUpdateItem[] = [
      {
        docPath,
        updateFn: () => ({
          state: newState,
        }),
      },
    ];

    try {
      await batchTransactionUpdate(firestoreInstance, updates, {
        ...options,
        logPrefix,
      });
    } catch (error: any) {
      console.error(
        `[Transaction:${logPrefix}] boardId=${boardId} operation=updateEmployeeState Failed: ${error?.code || 'UNKNOWN'} retryable=${error?.isRetryable ?? false}`
      );
      throw error;
    }
  }

  /**
   * Update multiple employees with new state and order (used by saveBoard)
   * 
   * @param boardId Board document ID
   * @param activeEmps Active employees array
   * @param inactiveEmps Inactive employees array
   * @param boardUpdates Additional board document updates (up, onDeck)
   * @param options Transaction configuration options
   * @returns Promise resolving when all updates are atomic
   */
  async saveBoard(
    boardId: string,
    activeEmps: EmployeeData[],
    inactiveEmps: EmployeeData[],
    boardUpdates: { up?: string; onDeck?: string },
    options?: TransactionOptions
  ): Promise<void> {
    const firestoreInstance = this.db.firestore as any;
    const logPrefix = options?.logPrefix || 'SaveBoard';

    console.log(`[Transaction:${logPrefix}] boardId=${boardId} operation=saveBoard activeCount=${activeEmps.length} inactiveCount=${inactiveEmps.length} Starting...`);

    // Build update items for active employees
    const updates: BatchUpdateItem[] = activeEmps.map((emp, index) => ({
      docPath: `boards/${boardId}/users/${emp.email || ''}`,
      updateFn: () => ({
        state: emp.state,
        order: index,
      }),
    }));

    // Add inactive employees
    inactiveEmps.forEach((emp, index) => {
      updates.push({
        docPath: `boards/${boardId}/users/${emp.email || ''}`,
        updateFn: () => ({
          state: emp.state,
          order: index,
        }),
      });
    });

    // Add board document update
    updates.push({
      docPath: `boards/${boardId}`,
      updateFn: () => ({
        up: boardUpdates.up ?? '',
        onDeck: boardUpdates.onDeck ?? '',
      }),
    });

    try {
      await batchTransactionUpdate(firestoreInstance, updates, {
        ...options,
        logPrefix,
      });
    } catch (error: any) {
      console.error(
        `[Transaction:${logPrefix}] boardId=${boardId} operation=saveBoard Failed: ${error?.code || 'UNKNOWN'} retryable=${error?.isRetryable ?? false}`
      );
      throw error;
    }
  }

  /**
   * Atomically reorder employees in the queue
   * 
   * Updates only the order field for all employees, preserving state values.
   * Useful for drag-and-drop reorder operations.
   * 
   * @param boardId Board document ID
   * @param reorderedEmps Employees in new order
   * @param options Transaction configuration options
   * @returns Promise resolving when reorder transaction completes
   */
  async reorderQueue(
    boardId: string,
    reorderedEmps: EmployeeData[],
    options?: TransactionOptions
  ): Promise<void> {
    const firestoreInstance = this.db.firestore as any;
    const logPrefix = options?.logPrefix || 'ReorderQueue';

    console.log(`[Transaction:${logPrefix}] boardId=${boardId} operation=reorderQueue employeeCount=${reorderedEmps.length} Starting...`);

    const updates: BatchUpdateItem[] = reorderedEmps.map((emp, index) => ({
      docPath: `boards/${boardId}/users/${emp.email || ''}`,
      updateFn: () => ({
        order: index,
      }),
    }));

    try {
      await batchTransactionUpdate(firestoreInstance, updates, {
        ...options,
        logPrefix,
      });
    } catch (error: any) {
      console.error(
        `[Transaction:${logPrefix}] boardId=${boardId} operation=reorderQueue Failed: ${error?.code || 'UNKNOWN'} retryable=${error?.isRetryable ?? false}`
      );
      throw error;
    }
  }
}
